import { Card, Heading, Stack, Text, textVariants } from '@etg/wings';
import { Notification, variants } from '@eti/components';
import { price } from '@eti/utils';
import { useProperty, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { ItineraryInfo } from '../../result/components/v3/drawer/itinerary';
import { TripDetails } from '../../result/components/v3/drawer/tripDetails/TripDetails';
import BaggageInformation from '../../result/components/v3/drawer/baggage/BaggageInformation';
import { CartRow, Price } from '../../../widgets/cart/components/CartComponents';
import type { TravelersByAgeType } from '../utils';
import { getTotalRebookingPrice } from '../utils';
import ContactMethods from '../../../common/components/ContactMethods';
import type { RebookEligibilityType } from '../../order-details/components/manage-trip/rebooking/models';
import { RebookEligibility } from '../../order-details/components/manage-trip/rebooking/models';
import type { RebookingTripType } from '../models';
import PriceBreakdownTravelerGroup from './PriceBreakdownTravelerGroup';

const drawerPricesInnerWrapperStyles = css`
  background-color: #e9f5ff;
  border-radius: 8px;
`;

const totalPriceStyles = css`
  font-size: 1rem;
  padding: 16px;
`;

const drawerStyles = css`
  border-radius: 8px;
  padding: 16px;
`;

interface RebookingTripDrawerContentProps {
  hasAirlineAncillaries: boolean;
  flight: RebookingTripType;
  priceDisplayText: string;
  pricesPerGroup: TravelersByAgeType[];
  rebookEligibilityType: RebookEligibilityType;
}

const RebookingTripDrawerContent = ({
  hasAirlineAncillaries,
  flight,
  priceDisplayText,
  pricesPerGroup,
  rebookEligibilityType,
}: RebookingTripDrawerContentProps) => {
  const { p } = useProperty();
  const { t } = useTranslation();

  const shouldShowItineraryInfo = p('Result.Page.Drawer.Itinerary.Info.Enabled');
  const shouldShowTripDetails = p('Result.Page.Drawer.Trip.Details.Enabled');
  const shouldShowBaggageInfo = p('Result.Page.Drawer.Baggage.Enabled');
  const isSeparate = p('Result.Page.V3.BaggageAccordion.IsSeparate');

  const getDrawerTextPerEligibilityType = (type: RebookEligibilityType): string => {
    switch (type) {
      case RebookEligibility.CHANGE_FLEXIBLE_TICKET_YOURSELF:
        return t('ChangeTrip.PriceDetailsDrawer.PriceSummary.FlexibleTicket.Description');
      default:
        return t('ChangeTrip.PriceDetailsDrawer.PriceSummary.Description');
    }
  };

  const drawerTextPerEligibilityType = getDrawerTextPerEligibilityType(rebookEligibilityType);

  const baggageInfo = {
    includedCabinBaggage: flight.includedCabinBaggage,
    includedCheckedBaggage: flight.includedCheckedBaggage,
    availableExtraProducts: [],
  };
  return (
    <>
      <Card>
        <Stack>
          {shouldShowItineraryInfo && (
            <ItineraryInfo
              bounds={flight.bounds}
              priceDisplayText={priceDisplayText}
              type={flight.type}
            />
          )}
          {shouldShowTripDetails && <TripDetails bounds={flight.bounds} tripType={flight.type} />}
          {!isSeparate && shouldShowBaggageInfo && (
            <BaggageInformation
              baggageInfo={baggageInfo}
              dataTestId="baggage-accordion-inside"
              isInDrawer
              isSeparate={isSeparate}
            />
          )}
        </Stack>
      </Card>
      {isSeparate && shouldShowBaggageInfo && (
        <BaggageInformation
          baggageInfo={baggageInfo}
          dataTestId="baggage-accordion-outside"
          isInDrawer
          isSeparate={isSeparate}
        />
      )}

      <Card data-testid="changeTrip-price-details-drawer-prices" id="changeTrip-price-details">
        <Stack>
          <Heading level={3}>{t('ChangeTrip.PriceDetailsDrawer.PriceSummary.Label')}</Heading>
          <Text>{drawerTextPerEligibilityType}</Text>
          <Stack className={drawerPricesInnerWrapperStyles} spacing={0}>
            {pricesPerGroup.map(
              ({
                ageType,
                airlineFees,
                airlinePenalty,
                fareDifference,
                quantity,
                serviceFee,
                taxDifference,
                totalDifference,
              }) => (
                <PriceBreakdownTravelerGroup
                  ageType={ageType}
                  airlineFees={airlineFees}
                  airlinePenalty={airlinePenalty}
                  className={drawerStyles}
                  fareDifference={fareDifference}
                  key={ageType}
                  lineSpacing={8}
                  quantity={quantity}
                  serviceFee={serviceFee}
                  taxDifference={taxDifference}
                  totalDifference={totalDifference}
                />
              ),
            )}
            <CartRow className={totalPriceStyles}>
              <Heading level={4}>
                {t('ChangeTrip.PriceDetailsDrawer.PriceSummary.TotalPrice.Text')}
              </Heading>
              <Price data-testid="changeTrip-priceBreakdown-overlay-summary-totalPrice">
                <Text variant={textVariants._14_BOLD}>
                  {price.formatPrice(getTotalRebookingPrice(pricesPerGroup))}
                </Text>
              </Price>
            </CartRow>
          </Stack>
          {hasAirlineAncillaries && (
            <Notification
              dataTestId="price-review-ancillaries-notification"
              variant={variants.WARNING}
            >
              <Text asHtml>{t('ChangeTrip.PriceDetailsDrawer.AncillariesNotification')}</Text>
            </Notification>
          )}
        </Stack>
      </Card>

      <Card>
        <Stack data-testid="changeTrip-price-details-drawer-contact">
          <Heading level={3}>{t('ChangeTrip.PriceDetailsDrawer.ContactMethods.Label')}</Heading>
          <Text>{t('ChangeTrip.PriceDetailsDrawer.ContactMethods.Description')}</Text>
          <ContactMethods chatType="genesys" shouldShowChat={p('Chat.Widget.Rebooking.Enabled')} />
        </Stack>
      </Card>
    </>
  );
};

export default RebookingTripDrawerContent;
